import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";


export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-heading text-[22px] leading-tight font-bold text-[#1B2B4B] tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`} {...rest}>{children}</div>;
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && <Icon className="w-10 h-10 text-slate-300 mb-3" />}
      <p className="text-slate-600 font-medium">{title}</p>
      {hint && <p className="text-sm text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function isPaidUser(user) {
  return !!(user && typeof user === "object" && ((user.subscription || {}).status === "active" || user.pro_gratuito));
}

// Botón de función premium: si el usuario no tiene plan de pago, se muestra atenuado
// con candado y tooltip, y al pulsarlo informa en lugar de ejecutar la acción.
export function PremiumButton({ paid, onClick, children, lockedHint = "Función de pago. Activa un plan para usarla.", className = "", ...rest }) {
  if (paid) {
    return <Button onClick={onClick} className={className} {...rest}>{children}</Button>;
  }
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toast.info(lockedHint); }}
            className={`opacity-50 cursor-not-allowed ${className}`}
            data-premium-locked="true"
            {...rest}
          >
            <Lock className="w-3.5 h-3.5 mr-1" />{children}
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[220px] text-center">{lockedHint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Aviso de "función de pago" para páginas premium en el Plan Gratuito.
export function PremiumBanner({ feature = "Esta función" }) {
  const navigate = useNavigate();
  return (
    <div className="mb-5 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="premium-banner">
      <div className="flex items-center gap-2 min-w-0">
        <Lock className="w-5 h-5 text-amber-600 shrink-0" />
        <p className="text-sm text-amber-800"><b>Función de pago.</b> {feature} está disponible con un plan activo. Puedes verla, pero las acciones requieren suscripción.</p>
      </div>
      <Button onClick={() => navigate("/configuracion")} className="bg-[#0A2540] hover:bg-[#031B33] text-white shrink-0" data-testid="premium-banner-cta">Ver planes</Button>
    </div>
  );
}
